import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { 
  Scan, 
  Cpu, 
  Layers, 
  ShieldCheck, 
  Zap, 
  Monitor, 
  Microscope,
  CheckCircle2,
  Award
} from "lucide-react";
import image1 from '../../Assets/Services/Service11-2.jpg'
import image2 from '../../Assets/Services/Service11-3.jpg'
import { Helmet } from "react-helmet";

const RadiologyDepartment = () => {
  return (
    <ServiceLayout
      title="Dental Radiology & Diagnostics"
      subtitle="“Advanced imaging technology for accurate diagnosis and confident dental care."
      heroImage="/images/hero/radiology-dept.jpg"
      images={[image1, image2]}
    >
      {/* Introduction & Heritage */}
      <section className="mb-12">
        <Helmet>
  <title>Dental Xray Vijayawada | Digital Scan Care</title>
  <meta name="description" content="Accurate dental diagnosis in Vijayawada with digital X-ray and radiology services for better treatment planning." />
  <meta name="keywords" content="dental xray Vijayawada, radiology" />
  <meta name="author" content="Dr Sridhar Dental" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://sridhardental.com/service/radiology-department" />
</Helmet>
        <div className="flex items-start gap-4 bg-blue-50 p-6 rounded-2xl border-l-8 border-blue-600 mb-8">
          <Award className="text-blue-600 w-12 h-12 flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-xl font-bold text-blue-900 mb-2">A Pioneer in Dental Diagnostics</h4>
            <p className="text-gray-700 leading-relaxed">
              Dr. Sridhar International Dental Hospital & Research Centre has been at the forefront of advanced
dental diagnostics in Andhra Pradesh. We were among the early dental hospitals in the region to
introduce full-mouth OPG X-ray diagnostic technology, setting new standards in dental imaging and
patient care.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Today, we continue this legacy by integrating state-of-the-art digital imaging systems that support
accurate diagnosis and precise treatment planning.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our Department of Dental Radiology is equipped with advanced imaging technology, enabling our
specialists to detect dental conditions with exceptional clarity, accuracy, and safety. This allows us to
plan treatments more effectively and deliver better outcomes for every patient.
            </p>
          </div>
        </div>
        <p className="text-gray-700 leading-relaxed">
          Our radiology department is continuously upgraded with high-standard modern dental imaging equipment, allowing our specialists to detect dental problems with exceptional clarity and accuracy.
        </p>
      </section>

      {/* Advanced Equipment Spotlight */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Advanced Radiology Equipment</h2>
        
        <div className="space-y-8">
          {/* CBCT System */}
          <div className="bg-white border border-blue-100 p-8 rounded-3xl shadow-sm hover:shadow-md transition">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/4 flex flex-col items-center justify-center bg-blue-50 rounded-2xl p-6">
                <Scan className="w-16 h-16 text-blue-600 mb-4" />
                <span className="text-xs font-black uppercase tracking-widest text-blue-800 text-center">Planmeca Romexis</span>
              </div>
              <div className="md:w-3/4">
                <h3 className="text-2xl font-bold text-blue-900 mb-3">CBCT – 3D Dental Imaging System</h3>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                  The Planmeca Romexis CBCT system provides highly detailed 3-dimensional images of teeth, jawbone structures, nerves, and sinuses. This is essential for complex surgical planning.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "High-resolution 3D visualization",
                    "Essential for dental implant planning",
                    "Lower radiation than conventional CT",
                    "Assists in TMJ and jaw disorders"
                  ].map((adv, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs font-bold text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-500" /> {adv}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* OPG System */}
          <div className="bg-white border border-gray-100 p-8 rounded-3xl shadow-sm hover:shadow-md transition">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/4 flex flex-col items-center justify-center bg-gray-50 rounded-2xl p-6">
                <Monitor className="w-16 h-16 text-gray-700 mb-4" />
                <span className="text-xs font-black uppercase tracking-widest text-gray-500 text-center">Vatech System</span>
              </div>
              <div className="md:w-3/4">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">OPG – Panoramic Full Mouth X-Ray</h3>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                  Captures a complete panoramic image of the entire mouth, including all teeth, jawbones, and surrounding structures in a single scan.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "Full mouth view in one scan",
                    "Detects impacted wisdom teeth",
                    "Diagnoses jaw fractures and cysts",
                    "Fast and comfortable process"
                  ].map((adv, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs font-bold text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-blue-500" /> {adv}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RVG Systems */}
          <div className="bg-gray-900 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row gap-8">
              <div className="md:w-1/3">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-2 !text-blue-400">
                  <Zap className="text-blue-400" /> Digital RVG Systems
                </h3>
                <p className="!text-white text-sm leading-relaxed mb-6">
                  We utilize modern digital intraoral X-ray technology from <strong>Sirona, Vatech, and Woodpecker</strong> for instant, high-quality imaging.
                </p>
              </div>
              <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "Instant digital X-ray images",
                  "Significantly reduced radiation",
                  "High-definition clarity",
                  "Easy digital record management"
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/10 p-4 rounded-xl border border-white/10">
                    <ShieldCheck className="text-blue-400 w-5 h-5 flex-shrink-0" />
                    <span className="text-xs font-semibold">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <Cpu className="absolute -bottom-10 -right-10 w-64 h-64 text-white/5" />
          </div>
        </div>
      </section>

      {/* Why Advanced Diagnosis Grid */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center">Benefits of Advanced Dental Radiology</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: <Microscope />, title: "Precise Accuracy", desc: "Detects hidden infections." },
            { icon: <ShieldCheck />, title: "Patient Safety", desc: "Minimal radiation exposure." },
            { icon: <Monitor />, title: "Instant Results", desc: "Faster treatment planning." },
            { icon: <Layers />, title: "3D Visualization", desc: "Detailed bone evaluation." },
          ].map((benefit, idx) => (
            <div key={idx} className="text-center group">
              <div className="text-blue-600 mb-4 flex justify-center transform group-hover:scale-110 transition-transform">
                {benefit.icon}
              </div>
              <h4 className="font-bold text-gray-900 text-sm mb-1">{benefit.title}</h4>
              <p className="text-xs text-gray-500 leading-tight">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Commitment Footer */}
      <section className="border-t pt-12 text-center max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-blue-900 mb-4 text-italic italic">Our commitment is to deliver accurate diagnosis that leads to better and more effective dental
treatment.”</h2>
        <p className="text-gray-600 leading-relaxed text-sm">
          Our highly trained specialists use advanced imaging technology to provide safe, fast, and reliable
diagnostic results. With state-of-the-art radiology systems and expert care, we ensure every patient
receives precise treatment planning and the highest standard of dental care.
        </p>
      </section>
    </ServiceLayout>
  );
};

export default RadiologyDepartment;